// OptionsChartJs.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LineController,
  TimeScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";
import io from "socket.io-client";
import { useStore } from "../states/store";

ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  TimeScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend
);

const OptionsChartJs = ({ height = 400, className = "" }) => {
  const { currentRound } = useStore();
  const chartRef = useRef(null);
  const socketRef = useRef(null);
  const markersRef = useRef([]); // [{ x: ms, label, color }]
  const markerIntervalRef = useRef(null);
  const cleanupIntervalRef = useRef(null);
  const latestPriceRef = useRef(null);
  const [initialized, setInitialized] = useState(false);

  // config
  const VISIBLE_BARS = 150;
  const MS_PER_BAR = 900;
  const WINDOW_MS = VISIBLE_BARS * MS_PER_BAR; // full window (we'll center on centerTime)
  const apiUrl = process.env.HL_API_v1_SERVER;
  const websocketUrl = process.env.HL_CRYPTO_v1_WS_SERVER;

  const normalizeTime = (t) => {
    const n = Number(t);
    if (Number.isNaN(n)) return Date.now();
    return n < 1e12 ? n * 1000 : n;
  };

  const getNextMarkerTime = () => {
    const now = new Date();
    const nextMarker = new Date(now.getTime());
    nextMarker.setMilliseconds(0);
    nextMarker.setSeconds(Math.floor(now.getSeconds() / 30) * 30 + 30, 0);
    return nextMarker.getTime();
  };

  // We'll pick a fixed centerTime when the chart initializes.
  // The window shown will be [centerTime - WINDOW_MS/2, centerTime + WINDOW_MS/2].
  const centerTimeRef = useRef(Date.now());

  // plugin draws vertical M30 / M40 lines based on markersRef
  const futureMarkersPlugin = {
    id: "futureMarkers",
    beforeDraw: (chart) => {
      const ctx = chart.ctx;
      const xScale = chart.scales.x;
      const top = chart.chartArea.top;
      const bottom = chart.chartArea.bottom;
      if (!xScale || !markersRef.current?.length) return;
      ctx.save();
      for (const marker of markersRef.current) {
        // marker.x is an absolute timestamp (ms)
        const x = xScale.getPixelForValue(marker.x);
        if (x < chart.chartArea.left - 60 || x > chart.chartArea.right + 60) continue;
        // vertical dashed line
        ctx.beginPath();
        ctx.strokeStyle = marker.color || "rgba(248,248,247,0.9)";
        ctx.lineWidth = 1;
        ctx.setLineDash([6, 4]);
        ctx.moveTo(x + 0.5, top);
        ctx.lineTo(x + 0.5, bottom);
        ctx.stroke();
        ctx.setLineDash([]);

        // label box
        const label = marker.label || "";
        const padding = 6;
        ctx.fillStyle = marker.color || "#c4fa02";
        ctx.font = "12px Inter, Arial";
        const textWidth = ctx.measureText(label).width;
        const rectW = Math.max(28, textWidth + padding * 2);
        const rectH = 18;
        const rectX = Math.max(chart.chartArea.left, Math.min(x - rectW / 2, chart.chartArea.right - rectW));
        const rectY = top + 8;
        ctx.fillRect(rectX, rectY, rectW, rectH);
        // text
        ctx.fillStyle = "#0b1220";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(label, rectX + rectW / 2, rectY + rectH / 2);
      }
      ctx.restore();
    },
  };

  // Register plugin once
  useEffect(() => {
    ChartJS.register(futureMarkersPlugin);
    return () => {
      try {
        ChartJS.unregister(futureMarkersPlugin);
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // initial data (historical) state
  const [data, setData] = useState({
    datasets: [
      {
        label: "BTCUSDT",
        data: [], // {x: ms, y: value}
        borderColor: "#c4fa02",
        backgroundColor: "rgba(196,250,2,0.08)",
        fill: "start",
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 1.5,
      },
    ],
  });

  // Chart options: fixed window around centerTimeRef.current
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(29,41,61,0.9)",
        borderColor: "#c4fa02",
        borderWidth: 1,
        titleColor: "#c4fa02",
        bodyColor: "#f0f0f0",
        callbacks: {
          label: (ctx) => {
            const v = ctx.raw?.y ?? ctx.raw;
            return `Value: ${Number(v).toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: { unit: "second", tooltipFormat: "HH:mm:ss" },
        ticks: { color: "#8F8F8F" },
        grid: { color: "rgba(255,255,255,0.04)" },
        // min/max will be set once data is in (centerTime is known)
      },
      y: {
        ticks: { color: "#f0f0f0" },
        grid: { color: "rgba(255,255,255,0.04)" },
      },
    },
  };

  // load history => map historical to the LEFT half of the window and add a single 'live' point at center
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      try {
        const nowMs = Date.now();
        const endTime = nowMs - 1;
        const startTime = endTime - WINDOW_MS;
        const resp = await fetch(
          `${apiUrl}/binance/historical?symbol=BTCUSDT&startTime=${startTime}&endTime=${endTime}&limit=${VISIBLE_BARS}`
        );

        let candles = [];
        if (resp.ok) {
          const json = await resp.json();
          candles = json
            .map((c) => {
              const t = normalizeTime(c.time ?? c.openTime ?? c.t ?? Date.now());
              const y = Number(c.close ?? c.price ?? c.value ?? c.y ?? NaN);
              return Number.isFinite(y) ? { t, y } : null;
            })
            .filter(Boolean)
            .sort((a, b) => a.t - b.t);
        }

        // fallback synthetic data
        if (candles.length === 0) {
          const dataFake = [];
          let y = 111425.16;
          const time = Date.now();
          for (let i = -VISIBLE_BARS; i <= 0; i++) {
            y += Math.random() * 10 - 5;
            dataFake.push({ t: time + i * MS_PER_BAR, y });
          }
          candles = dataFake;
        }

        if (!mounted) return;

        // Choose a center time (fixed for this session) - use now
        const center = Date.now();
        centerTimeRef.current = center;

        // Map historical candles evenly across the LEFT half of the window
        const N = candles.length;
        const leftSpan = WINDOW_MS / 2; // historical occupies left half
        const leftStart = center - leftSpan;
        let mapped = [];
        if (N === 1) {
          mapped = [{ x: leftStart, y: candles[0].y }];
        } else {
          for (let i = 0; i < N; i++) {
            const ratio = i / Math.max(1, N - 1);
            const x = Math.round(leftStart + ratio * leftSpan);
            mapped.push({ x, y: candles[i].y });
          }
        }

        // Add a single live point at center (will be updated by websocket)
        const lastY = candles.length ? candles[candles.length - 1].y : 0;
        const livePoint = { x: center, y: lastY };
        latestPriceRef.current = lastY;

        setData({
          datasets: [
            {
              ...data.datasets[0],
              data: [...mapped, livePoint],
            },
          ],
        });

        // set fixed min/max for x scale (centered)
        const chart = chartRef.current;
        if (chart && chart.options && chart.options.scales && chart.options.scales.x) {
          chart.options.scales.x.min = center - WINDOW_MS / 2;
          chart.options.scales.x.max = center + WINDOW_MS / 2;
        }

        setInitialized(true);
      } catch (err) {
        console.error("historical load failed", err);
        setInitialized(true);
      }
    };

    init();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // marker scheduling (M30 / M40) OR currentRound markers
  useEffect(() => {
    const pushMarker = (x, label, color) => {
      markersRef.current = markersRef.current.filter((m) => m.x !== x);
      markersRef.current.push({ x, label, color });
      // trigger chart redraw
      try {
        chartRef.current?.update();
      } catch (e) {}
    };

    const cleanupOld = () => {
      const ds = data.datasets[0].data;
      if (!ds?.length) return;
      const lastX = ds[ds.length - 1].x;
      markersRef.current = markersRef.current.filter((m) => m.x > lastX - 1000);
    };

    if (currentRound && currentRound.draw_open_time && currentRound.draw_close_time) {
      try {
        const openMs = normalizeTime(currentRound.draw_open_time);
        const closeMs = normalizeTime(currentRound.draw_close_time);
        pushMarker(openMs, "M30", "#f2ea07");
        pushMarker(closeMs, "M40", "#f2a807");
      } catch (e) {
        console.error("Failed to parse currentRound times", e);
      }
      return () => {};
    }

    const addFutureMarker = () => {
      const markerTime = getNextMarkerTime();
      pushMarker(markerTime, "M30", "#f2ea07");
      pushMarker(markerTime + 10000, "M40", "#f2a807");
    };

    // schedule markers
    addFutureMarker();
    markerIntervalRef.current = setInterval(addFutureMarker, 30000);
    cleanupIntervalRef.current = setInterval(cleanupOld, 1000);

    return () => {
      clearInterval(markerIntervalRef.current);
      clearInterval(cleanupIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, currentRound]);

  // websocket: update only the CENTER live point's y
  useEffect(() => {
    if (!initialized) return;
    const socket = io(`${websocketUrl}/hl_price`, {
      transports: ["websocket"],
      reconnectionAttempts: 5,
      reconnectionDelay: 5000,
    });
    socketRef.current = socket;

    socket.on("tradeUpdate", (data) => {
      const priceRaw = data?.value ?? data?.price ?? data?.p ?? data;
      const price = parseFloat(priceRaw);
      if (!Number.isFinite(price)) return;

      latestPriceRef.current = price;

      // update the last point (live point) in the dataset
      setData((prev) => {
        const ds = [...(prev.datasets[0].data || [])];
        if (!ds.length) return prev;
        const lastIdx = ds.length - 1;
        const updated = [...ds];
        updated[lastIdx] = { ...updated[lastIdx], y: price }; // keep same x (center)
        // do not push new x; keep historical static
        // return shallow updated state
        return {
          ...prev,
          datasets: [{ ...prev.datasets[0], data: updated }],
        };
      });

      // redraw without animation
      try {
        chartRef.current?.update("none");
      } catch (e) {}
    });

    socket.on("connect_error", (err) => console.error("WS err", err.message));
    socket.on("disconnect", () => console.log("WS disconnected"));

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  // redraw when markers change so plugin draws them
  useEffect(() => {
    try {
      chartRef.current?.update();
    } catch (e) {}
  }, [data, markersRef.current.length]);

  const wrapperStyle = {
    backgroundColor: "#1D293D",
    padding: 10,
    height,
  };

  return (
    <div style={wrapperStyle} className={className}>
      <h1 style={{ color: "white", marginBottom: 6 }}>Options Chart</h1>
      <div style={{ position: "relative", height: "100%" }}>
        <Line ref={chartRef} data={data} options={chartOptions} plugins={[futureMarkersPlugin]} />
      </div>
    </div>
  );
};

export default OptionsChartJs;
